/**
 * EN-1732: when the map's Tracksheet button (desktop) and tracksheet icon
 * (mobile) are shown.
 *
 * The tracksheet shows the selected live pig run, or the completed run picked
 * in the Pig Run Manager or Report Manager. With neither, it is empty, so the
 * button is hidden and an open tracksheet closes.
 */

/**
 * Lifecycle states of a run that counts as active for the tracksheet: running
 * ('R' from the API) or just launched by this browser ('A', a web-only
 * placeholder until the next active-runs fetch returns 'R'). Anything else
 * does not count: created ('C'), done ('D'), cancelled ('X'), no state or an
 * unknown code.
 */
const ACTIVE_LIFECYCLE_STATES: ReadonlyArray<string> = ['R', 'A'];

/** Whether a run in the live store counts as active for the tracksheet. */
export function isTracksheetLiveRun(
  run: { lifecycleState?: string | null } | null | undefined
): boolean {
  if (!run) return false;
  return ACTIVE_LIFECYCLE_STATES.includes(run.lifecycleState ?? '');
}

/** Whether at least one run in the list is active for the tracksheet. */
export function hasTracksheetLiveRun(
  runs:
    | ReadonlyArray<{ lifecycleState?: string | null } | null | undefined>
    | null
    | undefined
): boolean {
  return Array.isArray(runs) && runs.some(isTracksheetLiveRun);
}

/**
 * Whether the Tracksheet button shows: there is an active run, or a completed
 * run is selected in the Pig Run Manager or Report Manager (the tracksheet then
 * shows that run, so the completed-run flow keeps working).
 */
export function isTracksheetAvailable(
  runs:
    | ReadonlyArray<{ lifecycleState?: string | null } | null | undefined>
    | null
    | undefined,
  selectedCompletedPigRunId: number | null | undefined
): boolean {
  return (
    hasTracksheetLiveRun(runs) ||
    (selectedCompletedPigRunId !== null &&
      selectedCompletedPigRunId !== undefined)
  );
}
